/**
 * Bottom Megamenu JavaScript
 * Handles click interactions for bottom megamenu dropdowns and mobile modal
 */

class BottomMegaMenu {
  constructor() {
    this.activeDropdown = null;
    this.menuItems = [];
    this.isMobile = false;
    this.modal = null;
    this.modalOverlay = null;
    this.modalDirection = 'bottom-up';
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Check if mobile
    this.checkMobile();
    
    // Find all bottom megamenu containers
    const bottomMenus = document.querySelectorAll('.tpt-bottom-megamenu');
    
    bottomMenus.forEach(menu => {
      this.setupMenu(menu);
    });

    // Create modal for mobile
    this.createModal();

    // Handle clicks outside menu to close dropdowns
    document.addEventListener('click', (e) => this.handleOutsideClick(e));
    
    // Handle escape key to close dropdowns/modal
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeAllDropdowns();
        this.closeModal();
      }
    });

    // Handle window resize
    window.addEventListener('resize', () => {
      this.checkMobile();
    });
  }

  checkMobile() {
    this.isMobile = window.innerWidth <= 768;
  }

  setupMenu(menu) {
    // Get modal direction from data attribute
    this.modalDirection = menu.dataset.mobileModalDirection || 'bottom-up';
    
    const menuItems = menu.querySelectorAll('.tpt-bottom-megamenu__item-dropdown');
    
    menuItems.forEach(item => {
      const label = item.querySelector('.tpt-bottom-megamenu__item-label');
      const dropdown = item.querySelector('.tpt-bottom-megamenu__dropdown');
      
      if (label && dropdown) {
        // Store reference for later use
        this.menuItems.push({ item, label, dropdown });
        
        // Add click event listener
        label.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          if (this.isMobile) {
            this.openModal(item);
          } else {
            this.toggleDropdown(item);
          }
        });

        // Add keyboard accessibility
        label.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            
            if (this.isMobile) {
              this.openModal(item);
            } else {
              this.toggleDropdown(item);
            }
          }
        });

        // Make label focusable
        label.setAttribute('tabindex', '0');
        label.setAttribute('role', 'button');
        label.setAttribute('aria-haspopup', 'true');
        label.setAttribute('aria-expanded', 'false');
      }
    });
  }

  createModal() {
    // Create modal overlay
    this.modalOverlay = document.createElement('div');
    this.modalOverlay.className = 'tpt-bottom-megamenu__modal-overlay';
    this.modalOverlay.addEventListener('click', () => this.closeModal());

    // Create modal
    this.modal = document.createElement('div');
    this.modal.className = 'tpt-bottom-megamenu__modal tpt-megamenu';
    
    // Create modal content (no header here, will be created in openModal)
    const modalContent = document.createElement('div');
    modalContent.className = 'tpt-bottom-megamenu__modal-content';
    
    this.modal.appendChild(modalContent);
    
    // Append to body
    document.body.appendChild(this.modalOverlay);
    document.body.appendChild(this.modal);
    
    // Set initial direction class after appending to DOM
    this.updateModalDirection();
  }

  openModal(item) {
    if (!this.modal || !this.modalOverlay) {
      return;
    }
    
    const dropdown = item.querySelector('.tpt-bottom-megamenu__dropdown');
    const label = item.querySelector('.tpt-bottom-megamenu__item-label');
    const itemText = label.querySelector('.tpt-bottom-megamenu__item-text');
    
    if (!dropdown) return;
    
    // Clear modal content
    this.modal.innerHTML = '';
    
    // Create menu item (always expanded, no collapsing)
    const menuItem = document.createElement('div');
    menuItem.className = 'tpt-bottom-megamenu__modal-menu-item expanded';
    
    // Create menu header (this will be the modal header)
    const menuHeader = document.createElement('div');
    menuHeader.className = 'tpt-bottom-megamenu__modal-header tpt-bottom-megamenu__modal-menu-header';
    
    const menuTitle = document.createElement('h3');
    menuTitle.className = 'tpt-bottom-megamenu__modal-title tpt-bottom-megamenu__modal-menu-title';
    
    // Add icon if exists
    const itemIcon = label.querySelector('.tpt-bottom-megamenu__item-icon');
    if (itemIcon) {
      const iconClone = itemIcon.cloneNode(true);
      menuTitle.appendChild(iconClone);
    }
    
    // Add text
    const titleText = document.createElement('span');
    titleText.textContent = itemText ? itemText.textContent : 'Menu Item';
    menuTitle.appendChild(titleText);
    
    // Create close button
    const modalClose = document.createElement('button');
    modalClose.className = 'tpt-bottom-megamenu__modal-close';
    modalClose.innerHTML = '×';
    modalClose.addEventListener('click', (e) => {
      e.stopPropagation();
      this.closeModal();
    });
    
    menuHeader.appendChild(menuTitle);
    menuHeader.appendChild(modalClose);
    
    // Create menu content (always visible)
    const menuContent = document.createElement('div');
    menuContent.className = 'tpt-bottom-megamenu__modal-menu-content';
    
    const menuBlocks = document.createElement('div');
    menuBlocks.className = 'tpt-bottom-megamenu__modal-menu-blocks';
    
    // Clone dropdown grid content
    const originalGrid = dropdown.querySelector('.tpt-bottom-megamenu__grid');
    if (originalGrid) {
      menuBlocks.innerHTML = originalGrid.innerHTML;
    }
    
    menuContent.appendChild(menuBlocks);
    menuItem.appendChild(menuHeader);
    menuItem.appendChild(menuContent);
    
    // Add to modal
    this.modal.appendChild(menuItem);
    
    // Update modal direction before showing
    this.updateModalDirection();
    
    // Show modal with a small delay to ensure proper rendering
    requestAnimationFrame(() => {
      this.modalOverlay.classList.add('active');
      this.modal.classList.add('active');
      document.body.classList.add('tpt-bottom-modal-open');
    });
  }

  updateModalDirection() {
    if (!this.modal) return;

    // Reset all direction classes
    this.modal.classList.remove('modal-bottom-up', 'modal-left-right', 'modal-right-left');
    
    // Apply direction-specific class
    switch (this.modalDirection) {
      case 'left-right':
        this.modal.classList.add('modal-left-right');
        break;
        
      case 'right-left':
        this.modal.classList.add('modal-right-left');
        break;
        
      case 'bottom-up':
      default:
        this.modal.classList.add('modal-bottom-up');
        break;
    }
  }

  closeModal() {
    if (!this.modal || !this.modalOverlay) return;
    
    this.modalOverlay.classList.remove('active');
    this.modal.classList.remove('active');
    document.body.classList.remove('tpt-bottom-modal-open');
  }

  toggleDropdown(clickedItem) {
    if (this.isMobile) {
      this.openModal(clickedItem);
      return;
    }
    
    const dropdown = clickedItem.querySelector('.tpt-bottom-megamenu__dropdown');
    const label = clickedItem.querySelector('.tpt-bottom-megamenu__item-label');
    
    if (!dropdown || !label) return;

    const isCurrentlyActive = clickedItem.classList.contains('active');
    
    // Close all dropdowns first
    this.closeAllDropdowns();
    
    // If the clicked item wasn't active, open it
    if (!isCurrentlyActive) {
      this.openDropdown(clickedItem);
    }
  }

  openDropdown(item) {
    if (this.isMobile) return;
    
    const dropdown = item.querySelector('.tpt-bottom-megamenu__dropdown');
    const label = item.querySelector('.tpt-bottom-megamenu__item-label');
    
    if (!dropdown || !label) return;

    // Add active class
    item.classList.add('active');
    
    // Update ARIA attributes
    label.setAttribute('aria-expanded', 'true');
    
    // Position dropdown properly
    this.positionDropdown(item, dropdown);
    
    // Store reference to active dropdown
    this.activeDropdown = item;
    
    // Add animation class after a brief delay for smooth transition
    requestAnimationFrame(() => {
      dropdown.classList.add('tpt-bottom-megamenu__dropdown--visible');
    });
  }

  closeDropdown(item) {
    const dropdown = item.querySelector('.tpt-bottom-megamenu__dropdown');
    const label = item.querySelector('.tpt-bottom-megamenu__item-label');
    
    if (!dropdown || !label) return;

    // Remove active class
    item.classList.remove('active');
    dropdown.classList.remove('tpt-bottom-megamenu__dropdown--visible');
    
    // Update ARIA attributes
    label.setAttribute('aria-expanded', 'false');
    
    // Clear active dropdown reference if this was the active one
    if (this.activeDropdown === item) {
      this.activeDropdown = null;
    }
  }

  closeAllDropdowns() {
    this.menuItems.forEach(({ item }) => {
      this.closeDropdown(item);
    });
  }

  positionDropdown(item, dropdown) {
    const itemRect = item.getBoundingClientRect();
    const dropdownRect = dropdown.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Reset positioning classes
    dropdown.classList.remove(
      'tpt-bottom-megamenu__dropdown--left',
      'tpt-bottom-megamenu__dropdown--right',
      'tpt-bottom-megamenu__dropdown--center'
    );
    
    // Calculate horizontal positioning
    const itemCenterX = itemRect.left + itemRect.width / 2;
    const dropdownWidth = dropdownRect.width || 320; // fallback width
    
    // Check if dropdown would overflow on the right
    if (itemCenterX + dropdownWidth / 2 > viewportWidth - 20) {
      dropdown.classList.add('tpt-bottom-megamenu__dropdown--right');
    }
    // Check if dropdown would overflow on the left
    else if (itemCenterX - dropdownWidth / 2 < 20) {
      dropdown.classList.add('tpt-bottom-megamenu__dropdown--left');
    }
    // Center align by default
    else {
      dropdown.classList.add('tpt-bottom-megamenu__dropdown--center');
    }
    
    // Ensure dropdown doesn't go below the viewport
    const dropdownHeight = dropdownRect.height || 400; // fallback height
    if (itemRect.top - dropdownHeight < 20) {
      dropdown.style.maxHeight = (itemRect.top - 20) + 'px';
      dropdown.style.overflowY = 'auto';
    }
  }

  handleOutsideClick(e) {
    // Check if click was outside the megamenu and modal
    if (!e.target.closest('.tpt-bottom-megamenu') && !e.target.closest('.tpt-bottom-megamenu__modal')) {
      this.closeAllDropdowns();
      this.closeModal();
    }
  }

  // Public method to manually close all dropdowns and modal
  close() {
    this.closeAllDropdowns();
    this.closeModal();
  }
  
  // Public method to manually open a specific dropdown
  open(itemIndex) {
    if (this.menuItems[itemIndex]) {
      this.closeAllDropdowns();
      this.closeModal();
      
      if (this.isMobile) {
        this.openModal(this.menuItems[itemIndex].item);
      } else {
        this.openDropdown(this.menuItems[itemIndex].item);
      }
    }
  }
}

// Initialize when DOM is ready
const bottomMegaMenu = new BottomMegaMenu();

// Export for potential external use
window.BottomMegaMenu = BottomMegaMenu;